import { useQuery } from '@tanstack/react-query';
import { mcpCall } from '../../lib/mcp';
import { money, orDash } from '../../lib/format';
import { ProductThumb } from './ProductThumb';
import type { SkuRow } from './ProductsCatalog';

interface AuditEntry {
  id: number;
  actor_user_id: number | null;
  actor_label: string | null;
  action: string;
  target_type: string;
  target_id: number;
  created_at: string;
}

interface SkuDetailProps {
  row: SkuRow;
  /** Whether the operator's role may call listAuditLog — hides History if not. */
  canAudit: boolean;
}

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export function SkuDetail({ row, canAudit }: SkuDetailProps) {
  const auditQ = useQuery({
    queryKey: ['mcp', 'listAuditLog', { targetType: 'sku', targetId: row.id, limit: 5 }],
    queryFn: async () =>
      (
        await mcpCall<{ entries: AuditEntry[] }>('listAuditLog', {
          targetType: 'sku',
          targetId: row.id,
          limit: 5,
        })
      ).entries ?? [],
    enabled: canAudit,
  });

  const size = row.size;
  const dimensions = size ? `${size.length_in} × ${size.width_in} × ${size.depth_in} in` : null;

  return (
    <div className="catalog__detail">
      {/* Photo — inert placeholder until listSkus returns image_url (see ProductThumb). */}
      <section className="catalog__detail-block">
        <h3 className="catalog__detail-title">Photo</h3>
        <ProductThumb url={row.image_url} alt={row.sku_code} size="lg" />
      </section>

      {/* Size & specs */}
      <section className="catalog__detail-block">
        <h3 className="catalog__detail-title">Size &amp; specs</h3>
        <dl className="catalog__dl">
          {size && (
            <div className="catalog__dl-row">
              <dt>Size</dt>
              <dd>
                {size.nominal_label}
                {dimensions && <span className="catalog__dim"> ({dimensions})</span>}
              </dd>
            </div>
          )}
          <div className="catalog__dl-row">
            <dt>MERV</dt>
            <dd>{row.merv ?? '—'}</dd>
          </div>
          <div className="catalog__dl-row">
            <dt>Pack</dt>
            <dd>{row.pack_count}-pack</dd>
          </div>
          <div className="catalog__dl-row">
            <dt>Status</dt>
            <dd>
              <span className={`records__tag records__tag--${row.status === 'active' ? 'ok' : 'warn'}`}>
                {row.status}
              </span>
            </dd>
          </div>
        </dl>
      </section>

      {/* Pricing */}
      <section className="catalog__detail-block">
        <h3 className="catalog__detail-title">Pricing</h3>
        <dl className="catalog__dl">
          <div className="catalog__dl-row">
            <dt>Price</dt>
            <dd>{money(row.price)}</dd>
          </div>
          <div className="catalog__dl-row">
            <dt>Compare-at</dt>
            <dd>{money(row.compare_at_price)}</dd>
          </div>
        </dl>
      </section>

      {/* Listing & identifiers */}
      <section className="catalog__detail-block">
        <h3 className="catalog__detail-title">Listing &amp; identifiers</h3>
        <dl className="catalog__dl">
          <div className="catalog__dl-row">
            <dt>UPC</dt>
            <dd>{orDash(row.upc)}</dd>
          </div>
          <div className="catalog__dl-row">
            <dt>Amazon</dt>
            <dd>
              <span className={`records__tag records__tag--${row.amazon_asin ? 'ok' : 'warn'}`}>
                {row.amazon_asin ? 'Listed' : 'Missing'}
              </span>
              {row.amazon_asin && <span className="catalog__id">{row.amazon_asin}</span>}
            </dd>
          </div>
          <div className="catalog__dl-row">
            <dt>Shopify (historical)</dt>
            <dd>
              <span className={`records__tag records__tag--${row.shopify_id ? 'ok' : 'warn'}`}>
                {row.shopify_id ? 'Linked' : 'Missing'}
              </span>
              {row.shopify_id && <span className="catalog__id">{row.shopify_id}</span>}
            </dd>
          </div>
        </dl>
      </section>

      {/* Recent changes — only when the role can read the audit log */}
      {canAudit && (
        <section className="catalog__detail-block">
          <h3 className="catalog__detail-title">Recent changes</h3>
          {auditQ.isPending && <p className="catalog__detail-muted">Loading…</p>}
          {auditQ.isError && (
            <p className="catalog__detail-muted">Couldn’t load change history.</p>
          )}
          {auditQ.isSuccess && auditQ.data.length === 0 && (
            <p className="catalog__detail-muted">No recorded changes.</p>
          )}
          {auditQ.isSuccess && auditQ.data.length > 0 && (
            <ul className="catalog__history">
              {auditQ.data.map((e) => (
                <li key={e.id} className="catalog__history-item">
                  <span className="catalog__history-action">{e.action}</span>
                  <span className="catalog__history-meta">
                    {orDash(e.actor_label)} · {formatWhen(e.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
